import { useEffect, useState } from 'react';
import type { CatalogFacets, Category } from '@skytech/shared';

import { PriceRangeSlider } from './PriceRangeSlider';

export interface FilterState {
  category?: string;
  brand: string[];
  minPrice?: number;
  maxPrice?: number;
  inStock: boolean;
  minRating?: number;
}

/** Fallback brand list used only until the catalog facets load. */
const FALLBACK_BRANDS = ['Hikvision', 'Dahua', 'TP-Link', 'Ubiquiti', 'MikroTik'];

/**
 * Catalog filters (SRS §4.3): a collapsible category tree, a live price-range slider,
 * data-driven brands with counts, availability and rating. Renders as a sidebar on desktop
 * and inside a drawer on mobile (same component). All changes flow up to the page, which
 * mirrors them into the URL for shareable views.
 */
export function FilterPanel({
  categories,
  facets,
  value,
  onChange,
}: {
  categories: Category[];
  facets?: CatalogFacets;
  value: FilterState;
  onChange: (next: FilterState) => void;
}): JSX.Element {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  // Keep the branch that contains the active category open.
  useEffect(() => {
    if (!value.category) return;
    const anc = ancestorIds(categories, value.category);
    if (anc.length) setExpanded((prev) => new Set([...prev, ...anc]));
  }, [value.category, categories]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const brandOptions = facets
    ? facets.brands
    : FALLBACK_BRANDS.map((value) => ({ value, count: undefined as number | undefined }));

  const bounds = { min: facets?.priceMin ?? 0, max: facets?.priceMax ?? 0 };

  return (
    <div className="space-y-5">
      <Section title="Category">
        <ul className="space-y-1">
          <li>
            <button
              type="button"
              onClick={() => onChange({ ...value, category: undefined })}
              className={`w-full rounded px-2 py-1 text-left text-sm ${
                !value.category
                  ? 'bg-sky-500/[0.08] font-medium text-navy-900'
                  : 'text-navy-700 hover:bg-cloud'
              }`}
            >
              All categories
            </button>
          </li>
          {categories.map((cat) => (
            <CategoryNode
              key={cat.id}
              cat={cat}
              depth={0}
              value={value}
              onChange={onChange}
              expanded={expanded}
              toggle={toggle}
            />
          ))}
        </ul>
      </Section>

      <Section title="Price (KES)">
        {bounds.max > bounds.min ? (
          <PriceRangeSlider
            bounds={bounds}
            value={{ min: value.minPrice, max: value.maxPrice }}
            onChange={({ min, max }) =>
              onChange({
                ...value,
                minPrice: min > bounds.min ? min : undefined,
                maxPrice: max < bounds.max ? max : undefined,
              })
            }
          />
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              placeholder="Min"
              value={value.minPrice ?? ''}
              onChange={(e) =>
                onChange({
                  ...value,
                  minPrice: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              className="w-full rounded-md border border-frost px-2 py-1.5 text-sm"
              aria-label="Minimum price"
            />
            <span className="text-neutral">–</span>
            <input
              type="number"
              min={0}
              placeholder="Max"
              value={value.maxPrice ?? ''}
              onChange={(e) =>
                onChange({
                  ...value,
                  maxPrice: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              className="w-full rounded-md border border-frost px-2 py-1.5 text-sm"
              aria-label="Maximum price"
            />
          </div>
        )}
      </Section>

      {brandOptions.length > 0 && (
        <Section title="Brand">
          <ul className="space-y-1.5">
            {brandOptions.map(({ value: brand, count }) => {
              const checked = value.brand.includes(brand);
              return (
                <li key={brand}>
                  <label className="flex min-h-10 items-center gap-2 rounded-lg px-2 text-sm text-navy-700 hover:bg-cloud">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        onChange({
                          ...value,
                          brand: checked
                            ? value.brand.filter((b) => b !== brand)
                            : [...value.brand, brand],
                        })
                      }
                      className="h-4 w-4 accent-[var(--color-sky-600)]"
                    />
                    <span className="flex-1">{brand}</span>
                    {count !== undefined && <span className="text-xs text-neutral">{count}</span>}
                  </label>
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      <Section title="Availability">
        <label className="flex min-h-10 items-center gap-2 rounded-lg px-2 text-sm text-navy-700 hover:bg-cloud">
          <input
            type="checkbox"
            checked={value.inStock}
            onChange={(e) => onChange({ ...value, inStock: e.target.checked })}
            className="h-4 w-4 accent-[var(--color-sky-600)]"
          />
          In stock only
        </label>
      </Section>

      <Section title="Rating">
        <div className="flex flex-wrap gap-2">
          {[4, 3, 2].map((r) => (
            <button
              key={r}
              type="button"
              onClick={() =>
                onChange({ ...value, minRating: value.minRating === r ? undefined : r })
              }
              className={`min-h-9 rounded-full border px-3 py-1 text-xs ${
                value.minRating === r
                  ? 'border-sky-500 bg-sky-500/[0.06] text-sky-ink'
                  : 'border-frost text-navy-700 hover:border-sky-500'
              }`}
            >
              {r}★ &amp; up
            </button>
          ))}
        </div>
      </Section>
    </div>
  );
}

/** One node of the collapsible category tree; recurses into children. */
function CategoryNode({
  cat,
  depth,
  value,
  onChange,
  expanded,
  toggle,
}: {
  cat: Category;
  depth: number;
  value: FilterState;
  onChange: (next: FilterState) => void;
  expanded: Set<string>;
  toggle: (id: string) => void;
}): JSX.Element {
  const hasChildren = (cat.children?.length ?? 0) > 0;
  const isActive = value.category === cat.slug;
  const isOpen = expanded.has(cat.id);

  return (
    <li>
      <div className="flex items-center" style={{ paddingLeft: depth * 10 }}>
        {hasChildren ? (
          <button
            type="button"
            onClick={() => toggle(cat.id)}
            aria-label={isOpen ? `Collapse ${cat.name}` : `Expand ${cat.name}`}
            aria-expanded={isOpen}
            className="tap-target flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-neutral hover:bg-cloud hover:text-navy-900"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              aria-hidden
              className={`transition-transform ${isOpen ? 'rotate-90' : ''}`}
            >
              <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : (
          <span className="w-10 shrink-0" aria-hidden />
        )}
        <button
          type="button"
          onClick={() => onChange({ ...value, category: cat.slug })}
          className={`flex min-h-10 flex-1 items-center justify-between gap-2 rounded-lg px-2 py-2 text-left text-sm ${
            isActive ? 'bg-sky-500/[0.08] font-medium text-navy-900' : 'text-navy-700 hover:bg-cloud'
          }`}
        >
          <span>{cat.name}</span>
          <span className="text-xs text-neutral">{cat.totalProductCount}</span>
        </button>
      </div>
      {hasChildren && isOpen && (
        <ul className="mt-1 space-y-1">
          {cat.children!.map((ch) => (
            <CategoryNode
              key={ch.id}
              cat={ch}
              depth={depth + 1}
              value={value}
              onChange={onChange}
              expanded={expanded}
              toggle={toggle}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="border-t border-frost pt-4 first:border-t-0 first:pt-0">
      <h3 className="mb-2 text-xs font-medium uppercase tracking-[0.1em] text-navy-700">{title}</h3>
      {children}
    </div>
  );
}

/** ids of every ancestor of the category with the given slug (for auto-expanding its branch). */
function ancestorIds(categories: Category[], slug: string): string[] {
  const path: string[] = [];
  const walk = (nodes: Category[], trail: string[]): boolean => {
    for (const node of nodes) {
      if (node.slug === slug) {
        path.push(...trail);
        return true;
      }
      if (node.children?.length && walk(node.children, [...trail, node.id])) return true;
    }
    return false;
  };
  walk(categories, []);
  return path;
}
